import { DropdownValue } from "./types";

export const isObjectValue = (value: DropdownValue) =>
  typeof value === "object" && value !== null && "type" in value;

export const isJsonSchema = (value: DropdownValue) =>
  isObjectValue(value) && value.type === "json_schema";

export const optionMatchesDropdownValue = (
  optionValue: DropdownValue,
  selected: DropdownValue | undefined,
): boolean => {
  if (selected === undefined) {
    return false;
  }

  if (optionValue === selected) {
    return true;
  }

  if (optionValue === null && selected === null) {
    return true;
  }

  if (
    typeof optionValue === "object" &&
    optionValue !== null &&
    typeof selected === "object" &&
    selected !== null &&
    "type" in optionValue &&
    "type" in selected &&
    optionValue.type === "json_schema" &&
    selected.type === "json_schema"
  ) {
    return true;
  }

  return JSON.stringify(optionValue) === JSON.stringify(selected);
};

/** Radix Select disallows empty-string item values; use this for null options. */
export const NULL_SELECT_VALUE = "__promptlayer_null__";

export const getSelectItemValue = (value: DropdownValue | undefined): string => {
  if (value === null || value === undefined) {
    return NULL_SELECT_VALUE;
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "boolean") {
    return JSON.stringify(value);
  }
  return JSON.stringify(value);
};

export const resolveSelectOptionValue = (
  selected: DropdownValue | undefined,
  options: { display_name: string; value: DropdownValue }[],
  preferredDefault?: DropdownValue,
): DropdownValue | undefined => {
  if (selected === null || selected === undefined) {
    const nullOption = options.find(option => option.value === null);
    if (nullOption) {
      return nullOption.value;
    }
  } else {
    const matchingOption = options.find(option =>
      optionMatchesDropdownValue(option.value, selected),
    );
    if (matchingOption) {
      return matchingOption.value;
    }
  }

  // Stale/invalid selection (e.g. voice=0 after speech catalog changes).
  if (preferredDefault !== undefined) {
    const preferred = options.find(option =>
      optionMatchesDropdownValue(option.value, preferredDefault),
    );
    if (preferred) {
      return preferred.value;
    }
  }

  return options[0]?.value;
};

export const getSelectControlValue = (
  selected: DropdownValue | undefined,
  options: { display_name: string; value: DropdownValue }[],
  preferredDefault?: DropdownValue,
): string => {
  const resolved = resolveSelectOptionValue(selected, options, preferredDefault);

  if (resolved === undefined && (selected === null || selected === undefined)) {
    return getSelectItemValue(selected);
  }

  return getSelectItemValue(resolved ?? selected);
};
